import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import {SelectionModel} from '@angular/cdk/collections';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { Sort, MatSort, MatSortHeader } from '@angular/material/sort';
import {FireEvent, SelectOption, stagger60ms, TableColumn, TableColumnType, TableOrder, utils} from '../../../../global';
import {DateTransformPipe} from '../../../pipes';
import { NgIf, NgTemplateOutlet, NgFor, NgSwitch, NgSwitchCase } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatCheckbox } from '@angular/material/checkbox';
import { TableMenuComponent } from './elements/table-menu/table-menu.component';
import { TableDateComponent } from './elements/table-date/table-date.component';
import { TableTextComponent } from './elements/table-text/table-text.component';
import { TableIconTextComponent } from './elements/table-icon-text/table-icon-text.component';
import { TableUserComponent } from './elements/table-user/table-user.component';
import { TableTextButtonComponent } from './elements/table-text-button/table-text-button.component';
import { TableStepperComponent } from './elements/table-stepper/table-stepper.component';
import { ButtonComponent } from '../../buttons/button/button.component';
import { MatTooltip } from '@angular/material/tooltip';
import { TableEditableComponent } from './elements/table-editable/table-editable.component';
import { TableCounterTextComponent } from './elements/table-counter-text/table-counter-text.component';
import { TableSelectionButtonComponent } from './elements/selection-button-text/table-selection-button.component';
import { TableTextStatusComponent } from './elements/table-text-status/table-text-status.component';
import { ButtonIconComponent } from '../../buttons/button-icon/button-icon.component';
import { TableCounterIconComponent } from './elements/table-counter-icon/table-counter-icon.component';
import { SelectInputComponent } from '../../inputs/select-input/select-input.component';
import { LoaderComponent } from '../../loader/loader.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslatePipe } from '@ngx-translate/core';
import { CurrencyTransformPipe } from '../../../pipes/currency-transform.pipe';

@Component({
    selector: 'slm-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    animations: [stagger60ms],
    providers: [DateTransformPipe],
    imports: [NgIf, CdkScrollable, NgTemplateOutlet, MatTable, MatSort, NgFor, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCheckbox, MatCellDef, MatCell, MatSortHeader, TableMenuComponent, NgSwitch, NgSwitchCase, TableDateComponent, TableTextComponent, TableIconTextComponent, TableUserComponent, TableTextButtonComponent, TableStepperComponent, ButtonComponent, MatTooltip, TableEditableComponent, TableCounterTextComponent, TableSelectionButtonComponent, TableTextStatusComponent, ButtonIconComponent, TableCounterIconComponent, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, SelectInputComponent, LoaderComponent, SvgIconComponent, TranslatePipe, CurrencyTransformPipe]
})
export class TableComponent {
  private datePipe = inject(DateTransformPipe);

  @Input('config')
  public set setConfig(config: Array<TableColumn>) {
    this.config = (config || []).map(cnf => ({
      ...cnf,
      sortStart: !!cnf.sortStart ? cnf.sortStart : cnf.columnType === TableColumnType.DATE ? 'desc' : 'asc'
    }));
    this.columnNames = this.config.map((conf) => conf.attributeName);
    if (!!this.data) {
      this.setData = this.data;
    }
  }

  @Input() public selectedItem: any = null;
  @Input() public disableClearSort = false;
  @Input() public loading = true;
  @Input() public stickyHeader = false;

  @Input('sort')
  set setSort(sort: TableOrder) {
    if (!sort) {
      this.sort = null;
      return;
    }
    const sortBy = this.config.find(column => column.orderName === sort.attribute)?.attributeName;
    this.sort = {
      attribute: sortBy ?? sort.attribute,
      order: sort.order
    };
  }

  @Input() public selectionAttributes: Array<string> = ['id'];
  @Input() public placeholder = 'LBL_TABLE.NO_DATA';
  @Input() public groupSelectOptions: Array<SelectOption>;
  @Input() public groupColumns: Array<string>;

  private data: Array<any> = null;
  public sort: TableOrder;

  @Input('data')
  public set setData(data: Array<any>) {
    this.data = data;
    if (this.config[0]?.columnType === TableColumnType.CHECKBOX && !!this.config[0]?.notClickable) {
      const disableFunction = this.config[0]?.notClickable as ((any) => boolean);
      this.dataSource.data = this.transpileData((this.data || []).map(row => ({
        ...row,
        checkboxDisabled: disableFunction(row)
      })));
      this.masterCheckboxDisabled = this.getSelectableItems().length === 0;
    } else {
      this.dataSource.data = this.transpileData();
      this.masterCheckboxDisabled = false;
    }
    this.clearSelection();
  }

  public clearSelection() {
    this.selection.clear();
  }

  private transpileData(data: Array<any> = null) {
    const needToTranspile = (this.config || []).filter(config => !!config.transpile || !!config.color || config.tooltip);

    if (!needToTranspile.length || !this.data) {
      return this.data;
    }

    return (data || this.data || []).map(data => ({
      ...data,
      ...needToTranspile.reduce((prev, current) => ({
          ...prev,
          ...current.transpile ? {[current.attributeName]: current.transpile(data[current.attributeName])} : {},
          ...current.color ? {textColor: utils.isString(current.color) ? current.color : (current as any).color(data)} : {},
          ...current.tooltip || current.valueTooltip ? {
            [`tooltipLabel-${current.attributeName}`]: current.tooltip ?
              (utils.isString(current.tooltip) ? current.tooltip : (current as any).tooltip(data)) :
                current.columnType === TableColumnType.DATE ?
                  this.datePipe.transform(data[current.attributeName], current.dateFormat) :
                  data[current.attributeName]
          } : {},
          originalTableData: data
        }), {})
    }));
  }

  @Output() selected = new EventEmitter<any>();
  @Output() selectedAll = new EventEmitter<boolean>();
  @Output() menuClicked = new EventEmitter<FireEvent>();
  @Output() otherEvent = new EventEmitter<FireEvent>();
  @Output() itemClicked = new EventEmitter<any>();
  @Output() orderChanged = new EventEmitter<TableOrder>();
  @Output() itemMiddleClicked = new EventEmitter<any>();

  public readonly dataSource = new MatTableDataSource<any>([]);
  public readonly selection = new SelectionModel<any>(true, []);
  public columnTypes = TableColumnType;
  public config: Array<TableColumn> = [];
  public columnNames: Array<string> = [];
  public masterCheckboxDisabled = false;

  /** Whether the number of selected elements matches the total number of rows. */
  public isAllSelected() {
    const numSelected = this.selection?.selected?.length || 0;
    let numRows;
    if (this.config[0].columnType === TableColumnType.CHECKBOX && !!this.config[0].notClickable) {
      numRows = this.getSelectableItems().length;
    } else {
      numRows = this.dataSource?.data?.length || 0;
    }
    return numSelected === numRows;
  }

  public isGroupSelected(groupId: number) {
    const numSelected = (this.selection?.selected || []).filter(item => {
      return item.groupId === groupId;
    }).length;

    let numRows;
    if ((this.config[0].columnType === TableColumnType.CHECKBOX && !!this.config[0].notClickable) || !!groupId) {
      numRows = this.getGroupSelectableItems(groupId).length;
    } else {
      numRows = this.dataSource?.data?.length || 0;
    }

    return numSelected === numRows;
  }

  public hasSomeSelectedInGroup(groupId: number, fullyChecked: boolean) {
    return (this.selection?.selected || []).some(item => item.groupId === groupId) && (fullyChecked ?
      this.isGroupSelected(groupId) :
      !this.isGroupSelected(groupId));
  }

  private getSelectableItems() {
    return (this.dataSource.data || []).filter(row => !row.checkboxDisabled);
  }

  private getGroupSelectableItems(groupId: number) {
    return (this.dataSource.data || []).filter(row => !row.checkboxDisabled && row?.groupId === groupId);
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  public masterToggle() {
    if (this.isAllSelected()) {
      this.clearSelection();
    } else {
      if (this.config[0].columnType === TableColumnType.CHECKBOX && !!this.config[0].notClickable) {
        this.getSelectableItems().forEach((row) => this.selection.select(row));
        if (this.selection.selected.length === 0) {
          this.clearSelection();
        }
      } else {
        this.dataSource.data.forEach((row) => this.selection.select(row));
      }
    }
    this.emitSelection();
  }

  public groupToggle(groupId: number) {
    if (this.isGroupSelected(groupId)) {
      this.clearSelection();
    } else {
      if ((this.config[0].columnType === TableColumnType.CHECKBOX && !!this.config[0].notClickable) || !!groupId) {
        this.getGroupSelectableItems(groupId).forEach((row) => {
          return this.selection.select(row);
        });
        if (this.selection.selected.length === 0) {
          this.clearSelection();
        }
      } else {
        this.dataSource.data.forEach((row) => this.selection.select(row));
      }
    }
    this.emitSelection();
  }

  /** The label for the checkbox on the passed row */
  public checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.position + 1
    }`;
  }

  public groupCheckboxLabel(row?: any, groupId?: number): string {
    if (!row) {
      return `${this.isGroupSelected(groupId) ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.position + 1
    }`;
  }

  public menuEvent(event: string, item: any): void {
    this.menuClicked.emit({event, data: item.originalTableData ?? item});
  }

  public openItem(item: any): void {
    this.itemClicked.emit(item.originalTableData ?? item);
  }

  public isItemMiddleClicked(event: MouseEvent, item:any): void {
    if(event.button == 1){
      this.itemMiddleClicked.emit(item.originalTableData ?? item);
    }
  }

  public sortChanged(sort: Sort): void {
    const orderBy = (this.config || []).find(
      (config) => config.attributeName === sort.active
    );
    this.orderChanged.emit({
      order: this.disableClearSort && !sort.direction ? 'desc' : sort.direction,
      attribute: orderBy?.orderName || sort.active
    });
  }

  public selectionChanged(event, row): void {
    if (event) {
      this.selection.toggle(row);
    }
    this.emitSelection();
  }

  private emitSelection(): void {
    this.selectedAll.emit(this.isAllSelected());
    this.selected.emit(
      (this.selection.selected || []).map((selection) =>
        this.selectionAttributes && this.selectionAttributes.length
          ? this.getSelectedAttributes(selection)
          : selection
      )
    );
  }

  private getSelectedAttributes(selection: any) {
    return this.selectionAttributes.reduce((obj, attribute) => ({
      ...obj,
      [attribute]: selection.originalTableData ? selection.originalTableData[attribute] : selection[attribute]
    }), {});
  }

  public getTooltip = (attribute: string, data: any) => data.originalTableData ? data.originalTableData[attribute] : data[attribute];

  public tableEvent(event: any, attribute: string, identifier: any = null, row: any = null): void {
    this.otherEvent.emit({
      event: attribute, data: {
        ...event,
        ...identifier ? {identifier} : {},
        ...row ? {row} : {}
      }
    });
  }

  isGroup(_index, item): boolean{
    return item.isGroup;
  }
}
